import { FC, useEffect } from 'react';
import { Typography, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import WizardForm from '~/components/WizardForm';
import { useGetPayloadWithProvided } from '~/controllers/wizard';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { Stage } from '~/types/Application';
import { Draft } from '~/types/Draft';
import { LocationField, CountryField, CountryInput } from '~/components';
import buildNamePath from '~/utils/buildNamePath';
import LocationInput from '~/components/inputs/LocationInput';
import { isEnglish } from '~/utils/isEnglish';
import { useGetlocalizedValue } from '~/hooks/useGetlocalizedValue';
import { detectFieldIssue } from '~/utils/detectFieldIssue';
import ReviewItem from '../components/ReviewItem';
import ViewLocation from '../../application/address/components/ViewLocation';
import ReviewItemCheckbox from '../components/ReviewItemCheckbox';
import StepReviewComment from '../components/StepReviewComment';

const n = buildNamePath('reviewAddress');

const Address: FC = () => {
  const { t } = useTranslation('ksaReview');

  const [form] = WizardForm.useForm();

  const manualAddress = WizardForm.useWatch(n(['manual', 'enabled']), form);
  useEffect(() => {
    form.setFields([
      { name: n(['manual', 'city']), value: manualAddress },
      { name: n(['manual', 'addressLine']), value: manualAddress },
      { name: n(['manual', 'officePhoneNumber']), value: manualAddress },
      { name: n(['manual', 'poBox']), value: manualAddress },
      { name: n(['manual', 'email']), value: manualAddress },
    ]);
  }, [manualAddress, form]);

  const payload = useGetPayloadWithProvided();

  const address = payload?.address;
  const { localizedValue: localizedValueRegion, isReady: isReadyRegion }: any =
    useGetlocalizedValue(address?.location, 'region');
  const { localizedValue: localizedValueCountry, isReady: isReadyCountry }: any =
    useGetlocalizedValue(address?.country, 'country');
  const geolocation = payload?.geolocation;
  if (!payload) return null;

  return (
    <Container className="Address">
      <WizardForm<Draft<'reviewAddress'>>
        stage={Stage.ksaReview}
        payloadKey="reviewAddress"
        form={form}
      >
        <Typography.Title>{t('Address')}</Typography.Title>
        <Section>
          <InputsGroupLayout>
            <ReviewItem label={t('Country')}>
              <CountryField value={address?.country} />
            </ReviewItem>
            {isReadyCountry && (
              <WizardForm.Item
                label={t('Country')}
                name={n(['englishCountry'])}
                initialValue={isEnglish(localizedValueCountry) ? address?.country : undefined}
              >
                {/* Disabled when Thiqah value is already clean English — agent only needs to act when there is an issue */}
                <CountryInput disabled={!detectFieldIssue(localizedValueCountry)} />
              </WizardForm.Item>
            )}
            <ReviewItem label={t('Location')}>
              <LocationField value={address?.location} />
            </ReviewItem>
            {isReadyRegion && (
              <WizardForm.Item
                label={t('Location')}
                name={n(['englishLocation'])}
                initialValue={isEnglish(localizedValueRegion) ? address?.location : undefined}
              >
                <LocationInput disabled={!detectFieldIssue(localizedValueRegion)} />
              </WizardForm.Item>
            )}
            <ReviewItem label={t('City')}>{address?.city}</ReviewItem>
            <WizardForm.Item
              label={t('City')}
              name={n(['englishCity'])}
              initialValue={isEnglish(address?.city) ? address?.city : undefined}
            >
              <Input disabled={!detectFieldIssue(address?.city)} />
            </WizardForm.Item>
            <ReviewItem label={t('Address line')}>{address?.addressLine}</ReviewItem>
            <WizardForm.Item
              label={t('Address line')}
              name={n(['englishAddressLine'])}
              initialValue={isEnglish(address?.addressLine) ? address?.addressLine : undefined}
            >
              <Input disabled={!detectFieldIssue(address?.addressLine)} />
            </WizardForm.Item>
            <ReviewItem label={t('Office phone number')}>{address?.officePhoneNumber}</ReviewItem>
            {/* Risk Team requirement: allow agent to supply/correct phone when Thiqah returns it missing or in Arabic */}
            <WizardForm.Item
              labelText={t('Office phone number – Agent Updates')}
              name={n(['agentOfficePhoneNumber'])}
              initialValue={address?.officePhoneNumber || undefined}
            >
              <Input disabled={!detectFieldIssue(address?.officePhoneNumber)} placeholder={t('Enter office phone number')} />
            </WizardForm.Item>
            <ReviewItem label={t('Fax number')}>{address?.faxNumber}</ReviewItem>
            {/* Risk Team requirement: fax is returned by Thiqah but can be empty/Arabic */}
            <WizardForm.Item
              labelText={t('Fax number – Agent Updates')}
              name={n(['agentFaxNumber'])}
              initialValue={address?.faxNumber || undefined}
            >
              <Input disabled={!detectFieldIssue(address?.faxNumber)} placeholder={t('Enter fax number')} />
            </WizardForm.Item>
            <ReviewItem label={t('Additional phone number')}>
              {address?.additionalPhoneNumber}
            </ReviewItem>
            <ReviewItem label={t('Additional fax number')}>
              {address?.additionalFaxNumber}
            </ReviewItem>
            <ReviewItem label={t('P.O. Box')}>{address?.poBox}</ReviewItem>
            <WizardForm.Item
              labelText={t('P.O. Box – Agent Updates')}
              name={n(['agentPoBox'])}
              initialValue={address?.poBox || undefined}
            >
              <Input disabled={!detectFieldIssue(address?.poBox)} placeholder={t('Enter P.O. Box')} />
            </WizardForm.Item>
            <ReviewItem label={t('Company email')}>{address?.email}</ReviewItem>
            {/* Postal code is not returned by Thiqah so it always needs to be entered manually by the agent */}
            <WizardForm.Item
              labelText={t('Postal Code – Agent Updates')}
              name={n(['agentPostalCode'])}
              initialValue={undefined}
            >
              <Input placeholder={t('Enter postal code')} />
            </WizardForm.Item>
            {/* National Address sub-fields (building/street/district etc.) are required by the Risk Team
                but are not available from Thiqah, so the agent fills them in manually */}
            {/* <Typography.Title level={5} style={{ gridColumn: '1 / -1', marginTop: 16 }}>
              {t('National Address – Agent Updates')}
            </Typography.Title>
            <WizardForm.Item
              labelText={t('National Address – Building Number')}
              name={n(['nationalAddress', 'buildingNumber'])}
            >
              <Input placeholder={t('Enter building number')} />
            </WizardForm.Item>
            <WizardForm.Item
              labelText={t('National Address – Additional Number')}
              name={n(['nationalAddress', 'additionalNumber'])}
            >
              <Input placeholder={t('Enter additional number')} />
            </WizardForm.Item>
            <WizardForm.Item
              labelText={t('National Address – Unit Number')}
              name={n(['nationalAddress', 'unitNumber'])}
            >
              <Input placeholder={t('Enter unit number')} />
            </WizardForm.Item>
            <WizardForm.Item
              labelText={t('National Address – Street Name')}
              name={n(['nationalAddress', 'streetName'])}
            >
              <Input placeholder={t('Enter street name')} />
            </WizardForm.Item>
            <WizardForm.Item
              labelText={t('National Address – District Name')}
              name={n(['nationalAddress', 'districtName'])}
            >
              <Input placeholder={t('Enter district name')} />
            </WizardForm.Item>            
            <WizardForm.Item
              labelText={t('Regular Address – Agent Updates')}
              name={n(['agentRegularAddress'])}
            >
              <Input placeholder={t('Enter regular address')} />
            </WizardForm.Item> */}
          </InputsGroupLayout>
          <Typography.Title level={4}>{t('Manual address').toUpperCase()}</Typography.Title>
          <ReviewItemCheckbox
            // disabled
            label={t('Merchant wants to change a business address')}
            name={n(['manual', 'enabled'])}
          >
            <>{address?.manual?.enabled ? t('Yes') : t('No')}</>
          </ReviewItemCheckbox>
          <InputsGroupLayout>
            <ReviewItemCheckbox disabled name={n(['manual', 'city'])} label={t('City')}>
              {address?.manual?.city}
            </ReviewItemCheckbox>
            <WizardForm.Item
              label={t('City')}
              name={n(['manual', 'englishCity'])}
              initialValue={isEnglish(address?.manual?.city) ? address?.manual?.city : undefined}
            >
              <Input disabled={!manualAddress} />
            </WizardForm.Item>
            <ReviewItemCheckbox
              disabled
              name={n(['manual', 'addressLine'])}
              label={t('Address line')}
            >
              {address?.manual?.addressLine}
            </ReviewItemCheckbox>
            <WizardForm.Item
              label={t('Address line')}
              name={n(['manual', 'englishAddressLine'])}
              initialValue={
                isEnglish(address?.manual?.addressLine) ? address?.manual?.addressLine : undefined
              }
            >
              <Input disabled={!manualAddress} />
            </WizardForm.Item>
            <ReviewItemCheckbox
              disabled
              name={n(['manual', 'officePhoneNumber'])}
              label={t('Office phone number')}
            >
              <bdo dir="ltr">{address?.manual?.officePhoneNumber}</bdo>
            </ReviewItemCheckbox>
            <ReviewItemCheckbox disabled name={n(['manual', 'poBox'])} label={t('P.O. Box')}>
              {address?.manual?.poBox}
            </ReviewItemCheckbox>
            <ReviewItemCheckbox disabled name={n(['manual', 'email'])} label={t('Company email')}>
              {address?.manual?.email}
            </ReviewItemCheckbox>
          </InputsGroupLayout>
          {geolocation && <ViewLocation id="viewLocation" value={geolocation} />}
        </Section>
        <Section>
          <StepReviewComment n={n} />
        </Section>
      </WizardForm>
    </Container>
  );
};

const Container = styled.div``;

export default Address;
